import type { Channel } from '@takeit4me/shared'
import { describeBy, isStaffed, localParts, nextStaffedStart, type Closure, type Hours } from './time'

/**
 * The check step (design 8.6). Deterministic and policy checks run here, in
 * code. Model-reviewed checks (grounding, tone) are run by the API through
 * the model gateway and passed in as results. Any failure holds the reply;
 * takeit4me never sends anyway. Missing data never counts as a pass.
 */
export interface Check { name: string; ok: boolean; detail?: string }

export interface DraftForChecks {
  channel: Channel
  text: string
  company: { currency: string; allowed_domains: string[]; banned_phrases: string[]; disclosure: string; assistant_name: string }
  /** Money amounts the CRM supports for this conversation, in minor units. */
  known_amounts_minor: number[]
  /** Dates the CRM supports (YYYY-MM-DD). */
  known_dates: string[]
  /** Required wording for a regulated topic, if any. */
  required_wording: string[]
  /** Is this the first AI message to this customer on this channel in the conversation? */
  first_ai_message: boolean
  recipient_verified: boolean
  /** Personal data of other people that must not appear. */
  forbidden_values: string[]
  /** For natural dates ("Saturday 12 October", "12/10", "tomorrow"): the company's locale and zone, and now. */
  locale?: string
  timezone?: string
  now?: Date
  /** Promises the reply makes, what the company allows the AI to promise, and the actions policy approved. */
  promises?: { text: string; kind: string; due_at: string }[]
  allowed_promises?: { kind: string; max_hours?: number; needs_action?: string }[]
  approved_actions?: string[]
  /** When people are in (the conversation's site, else the company): promises a person must keep are checked against it. */
  staffed?: { hours: Hours; tz: string; closures?: Closure[] }
  /**
   * Did a live availability source answer on this turn (check_availability or the calendar)? When given, a reply
   * that says a time is or is not available without one is held (availabilityClaims).
   */
  availability_checked?: boolean
}

export const CHANNEL_LIMITS: Record<Channel, number> = { sms: 640, chat: 1500, voice: 900, email: 8000, whatsapp: 1500 }

const MONEY = /(?:A\$|AU\$|US\$|NZ\$|\$|£|€)\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?|\d+(?:\.\d{1,2})?)/g
const URL_RE = /\bhttps?:\/\/([^\s/<>"')]+)[^\s<>"')]*/gi
/**
 * Unfilled placeholders (never "[customer's name]"): template braces, any short bracketed phrase that names
 * something to fill in ("[customer's name]", "[Booking Reference]", "[Your Name]", "[insert date]", "(TBC)"), angle
 * tags such as <name>, and runs of X standing in for a number or time ("XX:XX", "$XX", "XXXX"). A Markdown link
 * label ("[our prices](https://...)") is not a placeholder.
 */
const FILL_WORDS = "name|first|surname|date|time|day|reference|ref|number|amount|price|cost|total|site|location|venue|centre|center|insert|your|customer|recipient|client|email|e-mail|phone|mobile|address|code|voucher|booking|order|link|url|todo|tbc|tbd|placeholder|details?|package|value"
const PLACEHOLDERS: RegExp[] = [
  /\{\{[^}]*\}\}/,
  new RegExp(`\\[[^\\]\\n]{0,40}\\b(?:${FILL_WORDS})\\b[^\\]\\n]{0,40}\\](?!\\()`, 'i'),
  /\[(?:TODO|TBC|TBD|\.\.\.|…|_+|\?+)\]/i,
  /\((?:TODO|TBC|TBD|insert[^)]{0,40})\)/i,
  /<(?:name|first[ _]?name|date|time|amount|reference|booking[ _]?ref(?:erence)?|code|link|email|phone)>/i,
  /(?<![A-Za-z])X{2,}(?::X{2})?(?![A-Za-z])/,
]
export function placeholdersIn(text: string): string[] {
  const out: string[] = []
  for (const re of PLACEHOLDERS) { const m = re.exec(text); if (m) out.push(m[0]) }
  return out
}
const CARD_REQUEST = /\b(card number|credit card|debit card|cvv|cvc|security code on (the|your) card|expiry date)\b/i
/**
 * Words a reply could be read as a person writing ("never say that you will personally handle anything, because
 * you are an AI"): claiming to be a person, or promising to do something personally or oneself. "We" is fine.
 */
const HUMAN_CLAIMS: RegExp[] = [
  /\b(i am|i'm)\s+(a\s+)?(real\s+)?(human|person|not an? (ai|bot|robot))\b/i,
  /\bI(?:'ll|’ll| will| shall|'m going to|’m going to| am going to| can| would|'d|’d)\s+(?:\w+\s+){0,2}?personally\b/i,
  /\bI(?:\s+have|'ve|’ve)?\s+personally\b/i,
  /\b(?:personally|myself)\s+(?:will\s+)?(?:make sure|ensure|look into|handle|deal with|take care of|check|call|ring|phone)\b/i,
  /\bI(?:'ll|’ll| will| am going to|'m going to|’m going to)\s+[^.!?\n]{0,40}\bmyself\b/i,
]
export function humanClaims(text: string): string[] {
  const out: string[] = []
  for (const re of HUMAN_CLAIMS) { const m = re.exec(text); if (m) out.push(m[0]) }
  return out
}

/**
 * Claims about booking availability ("never make claims that specific booking times are available or not, as
 * you have no access to the booking system"): a time, session or date said to be free, open, full or sold out, or a
 * claim to have checked the diary. Allowed only when the availability tool answered on this turn.
 */
const SLOT = '(?:slots?|sessions?|spaces?|places|spots?|times?|dates?|availability|bookings? (?:times?|slots?))'
const AVAILABILITY_CLAIMS: RegExp[] = [
  /\b(?:fully|completely)\s+booked\b|\bbooked (?:out|up)\b|\bsold out\b/i,
  new RegExp(`\\b(?:no|not any|any|plenty of|limited|a few|some)\\s+(?:more\\s+)?(?:free\\s+|available\\s+|open\\s+)?${SLOT}\\s+(?:left|available|free|remaining|open)\\b`, 'i'),
  new RegExp(`\\b(?:no|is|there's|there is|we have|we've got|still have|has)\\s+(?:still\\s+)?(?:some\\s+|good\\s+|plenty of\\s+)?availability\\b`, 'i'),
  new RegExp(`(?<!opening\\s)\\b${SLOT}\\s+(?:is|are)\\s+(?:still\\s+|currently\\s+|now\\s+)?(?:available|free|open|full|taken|gone|unavailable|not available)\\b`, 'i'),
  /\b\d{1,2}(?::\d{2})?\s?(?:am|pm)\b[^.!?\n]{0,30}\b(?:is|are)\s+(?:still\s+)?(?:available|free|full|taken|unavailable|not available)\b/i,
  /\b(?:mon|tues|wednes|thurs|fri|satur|sun)day\b[^.!?\n]{0,30}\b(?:is|are|looks?)\s+(?:still\s+)?(?:available|free|full|busy|taken|unavailable|not available|fully booked)\b/i,
  /\b(?:I|we)(?:'ve|’ve| have)?\s+(?:just\s+)?(?:checked|looked at|had a look at)\s+(?:the\s+|our\s+)?(?:availability|diary|calendar|booking system|bookings system|schedule)\b/i,
  /\b(?:there (?:are|is)|we have)\s+(?:no\s+|still\s+|some\s+|plenty of\s+)?(?:free\s+|available\s+|open\s+)?(?:slots?|spaces?|places|spots?)\b(?!\s+(?:to|for)\s+(?:park|parking|cars?|watch|spectat))/i,
]
export function availabilityClaims(text: string): string[] {
  const out: string[] = []
  for (const re of AVAILABILITY_CLAIMS) { const m = re.exec(text); if (m) out.push(m[0].trim()) }
  return out
}

export function moneyAmountsMinor(text: string): number[] {
  const out: number[] = []
  for (const m of text.matchAll(MONEY)) out.push(Math.round(Number(m[1].replace(/,/g, '')) * 100))
  return out
}

export function runDeterministicChecks(d: DraftForChecks): Check[] {
  const checks: Check[] = []
  const text = d.text ?? ''

  checks.push({ name: 'not_empty', ok: text.trim().length > 0, detail: text.trim() ? undefined : 'The reply is empty' })

  const amounts = moneyAmountsMinor(text)
  const unknown = amounts.filter((a) => !d.known_amounts_minor.includes(a))
  checks.push({
    name: 'amounts_match_crm', ok: unknown.length === 0,
    detail: unknown.length ? `Amounts not found in the records: ${unknown.map((a) => (a / 100).toFixed(2)).join(', ')}` : undefined,
  })

  const mentioned = datesInText(text, { locale: d.locale ?? 'en', timezone: d.timezone ?? 'UTC', now: d.now ?? new Date() })
  const known = new Set(d.known_dates.map((x) => x.slice(0, 10)))
  const badDates = mentioned.filter((m) => !known.has(m.date))
  checks.push({ name: 'dates_match_crm', ok: badDates.length === 0, detail: badDates.length ? `Dates not in the records: ${badDates.map((b) => `${b.text} (${b.date})`).join(', ')}` : undefined })

  const links = [...text.matchAll(URL_RE)].map((m) => m[1].toLowerCase().replace(/^www\./, ''))
  const badLinks = links.filter((host) => !d.company.allowed_domains.some((a) => host === a || host.endsWith('.' + a)))
  checks.push({ name: 'links_allowed', ok: badLinks.length === 0, detail: badLinks.length ? `Links to domains not on the allowed list: ${badLinks.join(', ')}` : undefined })

  const holes = placeholdersIn(text)
  checks.push({ name: 'no_placeholders', ok: holes.length === 0, detail: holes.length ? `An unfilled placeholder is left in the reply: ${holes.map((h) => `"${h}"`).join(', ')}` : undefined })

  checks.push({ name: 'recipient_verified', ok: d.recipient_verified, detail: d.recipient_verified ? undefined : 'The recipient comes from an unverified identifier' })

  const limit = CHANNEL_LIMITS[d.channel]
  checks.push({ name: 'length_suits_channel', ok: text.length <= limit, detail: text.length > limit ? `${text.length} characters is too long for ${d.channel} (limit ${limit})` : undefined })

  // Policy checks
  const banned = d.company.banned_phrases.filter((p) => p && text.toLowerCase().includes(p.toLowerCase()))
  checks.push({ name: 'no_banned_phrases', ok: banned.length === 0, detail: banned.length ? `Banned phrases: ${banned.join(', ')}` : undefined })

  const flat = normaliseWording(text)
  const missingWording = d.required_wording.filter((w) => w.trim() && !flat.includes(normaliseWording(w)))
  checks.push({ name: 'required_wording', ok: missingWording.length === 0, detail: missingWording.length ? `Required wording for this topic is missing: "${missingWording[0].slice(0, 120)}"` : undefined })

  if (d.allowed_promises) {
    const problems = promiseProblems(text, d.promises ?? [], d.allowed_promises, d.approved_actions ?? [], d.now ?? new Date())
    checks.push({ name: 'allowed_promises', ok: problems.length === 0, detail: problems.length ? problems.join('; ') : undefined })
  }
  if (d.staffed && d.promises?.length) {
    const problems = promiseHoursProblems(d.promises, d.staffed, d.now ?? new Date(), d.locale)
    checks.push({ name: 'promise_staffed_hours', ok: problems.length === 0, detail: problems.length ? problems.join('; ') : undefined })
  }

  if (d.first_ai_message) {
    const disclosed = /\b(AI|artificial intelligence|automated assistant|virtual assistant)\b/i.test(text) || text.includes(d.company.disclosure)
    checks.push({ name: 'ai_disclosure', ok: disclosed, detail: disclosed ? undefined : 'The first AI message must say it is an AI' })
  }

  const human = humanClaims(text)
  checks.push({ name: 'never_claims_human', ok: human.length === 0, detail: human.length ? `The reply reads as if a person wrote it ("${human[0]}"); use "we"` : undefined })
  if (d.availability_checked !== undefined) {
    const claims = d.availability_checked ? [] : availabilityClaims(text)
    checks.push({ name: 'no_availability_claims', ok: claims.length === 0, detail: claims.length ? `Says whether a time is available without a live availability check: "${claims[0]}"` : undefined })
  }
  if (d.channel === 'voice' || d.channel === 'chat' || d.channel === 'sms') {
    checks.push({ name: 'no_card_details', ok: !CARD_REQUEST.test(text), detail: CARD_REQUEST.test(text) ? 'Card details must only be taken through a secure payment link' : undefined })
  }

  const lower = text.toLowerCase()
  const digits = text.replace(/\D/g, '')
  const leaked = d.forbidden_values.filter((v) => {
    if (!v || v.length <= 3) return false
    if (lower.includes(v.toLowerCase())) return true
    const dv = v.replace(/\D/g, '')
    // Phone numbers match on their digits, whatever the spacing or the leading country code.
    return /^\+?[\d\s()-]{8,}$/.test(v) && dv.length >= 8 && digits.includes(dv.slice(-9))
  })
  checks.push({ name: 'no_other_personal_data', ok: leaked.length === 0, detail: leaked.length ? "The reply contains another person's details" : undefined })

  return checks
}

/** Signs of prompt injection in customer text (design 8.6 "Safety"). */
const INJECTION = [
  /ignore (all |any )?(previous|prior|above) (instructions|prompts)/i,
  /\b(system prompt|developer message|you are now|act as (an? )?(admin|developer|system))\b/i,
  /\bdisregard (your|the) (rules|instructions|policy)\b/i,
  /\b(reveal|print|show) (your|the) (instructions|prompt|system prompt)\b/i,
  /<\/?(system|assistant|tool)>/i,
]
export function injectionSignals(text: string): string[] {
  return INJECTION.filter((re) => re.test(text)).map((re) => re.source)
}

export function summarise(checks: Check[]) {
  const failed = checks.filter((c) => !c.ok)
  return { passed: failed.length === 0, failed: failed.map((c) => c.name), details: failed.map((c) => c.detail ?? c.name) }
}

// ---------------------------------------------------------------------------
// Natural dates (design 8.6 "dates match the CRM")
// ---------------------------------------------------------------------------
const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
}
const MONTH_RE = 'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?'
const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`
const valid = (y: number, m: number, d: number) => m >= 1 && m <= 12 && d >= 1 && d <= new Date(Date.UTC(y, m, 0)).getUTCDate()

/** Month-first locales write 10/12 for 12 October's opposite; everyone else is day-first. */
const MONTH_FIRST = /^en-(US|PH)$|^fil|^en-CA$/i

/**
 * Dates a reply states, as YYYY-MM-DD in the company's zone: ISO dates, "12 October", "October 12th",
 * "Saturday 12 Oct 2026", numeric "12/10" or "12/10/2026" (day first unless the locale is month first),
 * and "tomorrow". A date without a year is the next one on or after today (or within the last week).
 * Weekday names alone ("on Saturday") are not dates: opening hours mention them all the time.
 */
export function datesInText(text: string, o: { locale: string; timezone: string; now: Date }): { text: string; date: string }[] {
  const out: { text: string; date: string }[] = []
  const today = localParts(o.now, o.timezone)
  const yearFor = (m: number, d: number) => {
    const t = Date.UTC(today.year, today.month - 1, today.day)
    const cand = Date.UTC(today.year, m - 1, d)
    return cand < t - 7 * 86_400_000 ? today.year + 1 : today.year
  }
  const add = (raw: string, y: number | null, m: number, d: number) => {
    const year = y === null ? yearFor(m, d) : y < 100 ? 2000 + y : y
    if (valid(year, m, d)) out.push({ text: raw.trim(), date: ymd(year, m, d) })
  }
  const taken: [number, number][] = []
  const free = (i: number, len: number) => !taken.some(([a, b]) => i < b && i + len > a)
  const scan = (re: RegExp, fn: (m: RegExpMatchArray) => void) => {
    for (const m of text.matchAll(re)) if (free(m.index!, m[0].length)) { taken.push([m.index!, m.index! + m[0].length]); fn(m) }
  }
  scan(/\b(20\d{2})-(\d{2})-(\d{2})\b/g, (m) => add(m[0], Number(m[1]), Number(m[2]), Number(m[3])))
  scan(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTH_RE})\\.?(?:,?\\s+(20\\d{2}))?\\b`, 'gi'), (m) => add(m[0], m[3] ? Number(m[3]) : null, MONTHS[m[2].toLowerCase()], Number(m[1])))
  scan(new RegExp(`\\b(${MONTH_RE})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b(?:,?\\s+(20\\d{2}))?`, 'gi'), (m) => add(m[0], m[3] ? Number(m[3]) : null, MONTHS[m[1].toLowerCase()], Number(m[2])))
  const numeric = (m: RegExpMatchArray, a: number, b: number, y: string | undefined) => {
    const [d, mo] = MONTH_FIRST.test(o.locale) ? [b, a] : [a, b]
    add(m[0], y ? Number(y) : null, mo, d)
  }
  // With a year, a numeric date is a date anywhere; without one, only after a date word ("on 12/10"), so 24/7 and 1/2 are not.
  scan(/(?<![\d/.$£€])(\d{1,2})\/(\d{1,2})\/(\d{2}|20\d{2})(?![\d/])/g, (m) => numeric(m, Number(m[1]), Number(m[2]), m[3]))
  scan(/\b(?:on|by|from|until|till|before|after|for|dated?)\s+(?:(?:mon|tues|wednes|thurs|fri|satur|sun)day,?\s+)?(\d{1,2})\/(\d{1,2})(?![\d/])/gi, (m) => numeric(m, Number(m[1]), Number(m[2]), undefined))
  scan(/\btomorrow\b/gi, (m) => {
    const t = new Date(Date.UTC(today.year, today.month - 1, today.day + 1))
    out.push({ text: m[0], date: ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()) })
  })
  return out
}

// ---------------------------------------------------------------------------
// Wording and promises (design 8.6 "Policy")
// ---------------------------------------------------------------------------
/** Compares wording ignoring case, spacing and curly quotes, so a re-wrapped line still matches. */
export function normaliseWording(s: string) {
  return s.toLowerCase().replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/\s+/g, ' ').trim()
}

/** Commitments in the reply's own words, for promises the model did not declare. */
const PROMISE_LANGUAGE = /\b(?:i|we)(?:'ll| will| shall)\s+(?:(?:personally|also|then|definitely)\s+)?(?:call|ring|phone|email|text|send|refund|credit|get back|follow up|be in touch|contact you|arrange)\b/i

/** Promise kinds a person keeps: a call at a time, or a person getting back to the customer. */
export const STAFFED_PROMISE_KINDS = new Set(['callback', 'follow_up'])

/**
 * Promises a person must keep, checked against staffed hours (design 8.6, 12.5; A 5.2-13). A callback is at a time,
 * so that time must be staffed. A follow-up is "by" a time, so some staffed time must come before it. A promise
 * with no readable time is caught by `promiseProblems`.
 */
export function promiseHoursProblems(promises: { text: string; kind: string; due_at: string }[], s: { hours: Hours; tz: string; closures?: Closure[] }, now: Date, locale = 'en'): string[] {
  const out: string[] = []
  for (const p of promises) {
    if (!STAFFED_PROMISE_KINDS.has(p.kind)) continue
    const due = new Date(p.due_at)
    if (Number.isNaN(due.getTime())) continue
    const next = nextStaffedStart(now, s.hours, s.tz, s.closures)
    const nextText = next ? `; the team is next in ${describeBy(next, now, s.tz, locale).replace(/^by /, '')}` : '; no staffed hours are set'
    if (p.kind === 'callback' && !isStaffed(due, s.hours, s.tz, s.closures)) {
      out.push(`"${p.text.slice(0, 80)}" is a call at a time nobody is in to make it${nextText}`)
    } else if (p.kind === 'follow_up' && !isStaffed(now, s.hours, s.tz, s.closures) && (!next || next.getTime() >= due.getTime())) {
      out.push(`"${p.text.slice(0, 80)}" is due before anyone is in to keep it${nextText}`)
    }
  }
  return out
}

export function promiseProblems(text: string, promises: { text: string; kind: string; due_at: string }[], allowed: { kind: string; max_hours?: number; needs_action?: string }[], approvedActions: string[], now: Date): string[] {
  const out: string[] = []
  for (const p of promises) {
    const rule = allowed.find((a) => a.kind === p.kind)
    if (!rule) { out.push(`The AI may not promise this (${p.kind.replace('_', ' ')}): "${p.text.slice(0, 80)}"`); continue }
    const due = new Date(p.due_at)
    if (Number.isNaN(due.getTime())) { out.push(`The promise "${p.text.slice(0, 80)}" has no clear time`); continue }
    if (rule.max_hours !== undefined && due.getTime() - now.getTime() > rule.max_hours * 3_600_000) out.push(`"${p.text.slice(0, 80)}" is due later than the AI may promise (${rule.max_hours} hours)`)
    if (rule.needs_action && !approvedActions.includes(rule.needs_action)) out.push(`"${p.text.slice(0, 80)}" needs a ${rule.needs_action.replace('_', ' ')} that policy has approved`)
  }
  if (!promises.length && PROMISE_LANGUAGE.test(text)) out.push('The reply makes a commitment that was not declared as a promise')
  return out
}
